import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button';

const ProductManager = ({ shopId }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
  });
  const [file, setFile] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!shopId) return;
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          api.get(`/shops/${shopId}/products`),
          api.get(`/shops/${shopId}/categories`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        if (categoriesRes.data.length > 0) {
          setFormData((prev) => ({ ...prev, category: categoriesRes.data[0]._id }));
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [shopId]);

  const { name, description, price, category } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!shopId) {
      console.error("Shop ID is not available.");
      return;
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const productData = new FormData();
    productData.append('name', name);
    productData.append('description', description);
    productData.append('price', price);
    productData.append('category', category);
    if (file) {
      productData.append('productImage', file);
    }

    try {
      if (editingProduct) {
        const res = await api.put(`/products/${editingProduct._id}`, productData, config);
        setProducts(products.map(p => p._id === editingProduct._id ? res.data : p));
        setEditingProduct(null);
      } else {
        const res = await api.post(`/shops/${shopId}/products`, productData, config);
        setProducts([...products, res.data]);
      }
      setFormData({ name: '', description: '', price: '', category: categories.length > 0 ? categories[0]._id : '' });
      setFile(null);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setProducts(products.filter((product) => product._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
    });
    setFile(null);
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', price: '', category: categories.length > 0 ? categories[0]._id : '' });
    setFile(null);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">{editingProduct ? 'Edytuj Produkt' : 'Dodaj Nowy Produkt'}</h2>
      {categories.length === 0 ? (
        <p>Musisz najpierw dodać kategorię, aby móc dodać produkt.</p>
      ) : (
        <form onSubmit={onSubmit} className="mb-4 p-4 border rounded-lg">
          <input type="text" name="name" value={name} onChange={onChange} placeholder="Nazwa produktu" required className="input mb-2" />
          <textarea name="description" value={description} onChange={onChange} placeholder="Opis produktu" required className="input mb-2" />
          <input type="number" name="price" value={price} onChange={onChange} placeholder="Cena" required className="input mb-2" />
          <select name="category" value={category} onChange={onChange} required className="input mb-2">
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700">Obrazek Produktu</label>
            <input type="file" name="productImage" onChange={onFileChange} className="input" />
          </div>
          <div className="flex space-x-2">
            <Button type="submit">{editingProduct ? 'Zaktualizuj Produkt' : 'Dodaj Produkt'}</Button>
            {editingProduct && (
              <Button onClick={cancelEdit} variant="secondary" type="button">Anuluj</Button>
            )}
          </div>
        </form>
      )}
      <ul className="space-y-2">
        {products.map((product) => (
          <li key={product._id} className="flex justify-between items-center p-2 border rounded-lg">
            <div className="flex items-center">
              {product.imageUrl && <img src={`http://api.krukshop.ovh${product.imageUrl}`} alt={product.name} className="w-12 h-12 mr-4 object-cover rounded" />}
              <span>{product.name} - {product.price} PLN</span>
            </div>
            <div className="space-x-2">
              <Button onClick={() => startEdit(product)} variant="secondary">Edytuj</Button>
              <Button onClick={() => deleteProduct(product._id)} variant="danger">Usuń</Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProductManager;